import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuthStore } from '../store/auth.store.js';
import { BackgroundEffects } from '../components/lobby/BackgroundEffects.js';
import { OnlineStatus } from '../components/lobby/OnlineStatus.js';
import { RoomDetailsCard } from '../components/waiting-room/RoomDetailsCard.js';
import { PlayerListCard } from '../components/waiting-room/PlayerListCard.js';
import { TicketPreviewCard } from '../components/waiting-room/TicketPreviewCard.js';
import { CountdownPanel } from '../components/waiting-room/CountdownPanel.js';

import { useRoomStore } from '../store/room.store.js';

export function WaitingRoomScreen() {
  const { user, isSessionChecked } = useAuthStore();
  const { activeRoom, roomDetails, isLoading, leaveRoom, gameStarted, players } = useRoomStore();
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);

  // Route protection
  useEffect(() => {
    if (isSessionChecked && !user) {
      navigate('/login');
    }
  }, [user, isSessionChecked, navigate]);

  const currentPlayer = players.find((p) => p.id === activeRoom?.sessionId);
  const isHost = currentPlayer ? currentPlayer.isHost : false;

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (mounted && !activeRoom && !isLoading) {
      navigate('/home');
    }
  }, [mounted, activeRoom, isLoading, navigate]);

  // Navigate to game when host starts the game
  useEffect(() => {
    if (gameStarted) {
      navigate('/game');
    }
  }, [gameStarted, navigate]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      useRoomStore.getState().leaveRoom();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      
      const path = window.location.pathname;
      const isNavigatingToGame = path === '/game';
      const isStrictModeCleanup = path === '/waiting-room';
      if (!isNavigatingToGame && !isStrictModeCleanup) {
        useRoomStore.getState().leaveRoom();
      }
    };
  }, []);

  const handleLeave = () => {
    leaveRoom();
    navigate('/home');
  };

  // Stagger configurations
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
  };

  if (isLoading || !roomDetails) {
    return (
      <div className="relative flex h-screen w-screen items-center justify-center bg-game-bg">
        <BackgroundEffects />
        <div className="text-center space-y-4 z-20">
          <div className="w-12 h-12 rounded-full border-4 border-purple-500 border-t-transparent animate-spin mx-auto" />
          <p className="text-sm font-bold tracking-wider text-surface-200 uppercase animate-pulse">
            Connecting to Game Room...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex h-screen w-screen flex-col justify-between overflow-hidden select-none">
      {/* Lobby background particles & bingo shapes */}
      <BackgroundEffects />

      {/* Waiting Room Header */}
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="relative w-full flex items-center justify-between px-4 py-3 backdrop-blur-md border-b border-white/5 bg-slate-950/20 z-30 h-14"
      >
        {/* Back navigation */}
        <button
          onClick={handleLeave}
          className="flex items-center justify-center w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-surface-200 hover:text-white transition-colors cursor-pointer z-10"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>

        {/* Title */}
        <div className="absolute left-1/2 transform -translate-x-1/2 flex flex-col items-center text-center pointer-events-none whitespace-nowrap">
          <div className="flex items-center space-x-1.5 justify-center">
            <h2 className="font-display text-sm font-black tracking-wider text-white uppercase">
              {roomDetails.name}
            </h2>
            <span className="px-1.5 py-0.5 rounded bg-amber-500/10 border border-amber-500/20 text-[8px] font-black font-mono text-amber-400">
              {roomDetails.code}
            </span>
          </div>
        </div>

        {/* Connection Widget */}
        <div className="flex items-center z-10">
          {isHost && (
            <span className="text-[8px] font-black uppercase text-amber-300 bg-amber-950/40 border border-amber-500/20 px-1.5 py-0.5 rounded">
              Host
            </span>
          )}
        </div>
      </motion.header>

      {/* Main 3 columns layout */}
      <main className="flex-1 flex flex-col justify-center items-center px-6 py-4 max-h-[calc(100vh-140px)] z-20 overflow-y-auto">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-6xl w-full h-full max-h-[460px]"
        >
          {/* Column 1: Room Config Settings details */}
          <motion.div variants={itemVariants} className="h-full">
            <RoomDetailsCard
              maxPlayers={roomDetails.maxPlayers}
              gameSpeed={roomDetails.gameSpeed}
              callerMode={roomDetails.callerMode}
              prizePreset={roomDetails.prizePreset}
            />
          </motion.div>

          {/* Column 2: Joined Players Lists */}
          <motion.div variants={itemVariants} className="h-full">
            <PlayerListCard currentUser={user} />
          </motion.div>

          {/* Column 3: Generated Ticket previews */}
          <motion.div variants={itemVariants} className="h-full">
            <TicketPreviewCard />
          </motion.div>
        </motion.div>
      </main>

      {/* Bottom Footer & Countdown buttons */}
      <CountdownPanel roomCode={roomDetails.code} onLeave={handleLeave} />
    </div>
  );
}